import React, { useContext, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Button, Link, Typography } from '@one-thd/sui-atomic-components';
import {
  initialSmsTableData,
  PRO_TITLE, PRO_TYPE,
  PROMOTIONS_TITLE,
  PROMOTIONS_TYPE
} from './tableData';
import MarketingPreferencesCard from './common/MarketingPreferencesCard';
import {
  ALL_SMS_UNSUBSCRIBE_SUCCESS, NO, NO_PHONE_TEXT, PENDING, SECURITY_PATH, SMS_OPT_IN_SUCCESS, YES,
  PRO_PROMO_PENDING, PROMO_PENDING, PRO_PENDING
} from '../constants';
import {
  retrieveSmsPreferences, unsubscribeFromAllMessages,
  updateSmsPreferences
} from '../apis/sms';
import {
  convertSmsSubsToPrefs,
  getUnsubscribeList,
  handleRollback, setupHandleEmailOrSmsTelemarketingRetrieve
} from '../apis/common';
import { BannerContext, BannerProvider } from '../contexts/BannerContext';
import { addAnalyticEvent } from '../analytics/common';
import {
  confirmConsentEvent,
  confirmConsentOverlay,
  useAccountPhoneOverlay,
  unsubscribeFromAllSms
} from '../analytics/sms';
import ErrorBoundary from '../contexts/ErrorBoundary';
import ConfirmationModal from './SmsModals/ConfirmationModal';
import AccountPhoneModal from './SmsModals/AccountPhoneModal';
import { containsOptIn, scrollToBanner } from '../../utils/general';
import { formatPhone } from './common/helper';
import { RetrieveErrorContext } from '../contexts/RetrieveErrorContext';

export const confirmModalButton = 'sms-confirm-modal-button';
export const useAccountButton = 'sms-account-modal-button';
export const smsSaveButton = 'sms-save-button';
export const unsubSmsButton = 'sms-unsub';

const Actions = {
  RESET: 'RESET',
  USE_ACCOUNT_PHONE: 'USE_ACCOUNT_PHONE',
  CONFIRMATION_MODAL: 'CONFIRMATION_MODAL',
  UNSUBSCRIBE_FROM_ALL: 'UNSUBSCRIBE_FROM_ALL'
};

function initialState() {
  return {
    noPhoneModalHeader: '',
    showUseAccountPhone: false,
    showConfirmationModal: false,
    showProLegal: false,
    showPromoLegal: false,
    displayUnsubscribeModal: false
  };
}

function reducer(state, action) {
  switch (action.type) {
  case Actions.USE_ACCOUNT_PHONE:
    addAnalyticEvent(useAccountPhoneOverlay);
    return {
      ...state,
      showUseAccountPhone: true
    };
  case Actions.CONFIRMATION_MODAL:
    addAnalyticEvent(confirmConsentOverlay);
    return {
      ...state,
      showUseAccountPhone: false,
      showProLegal: action.showProLegal,
      showPromoLegal: action.showPromoLegal,
      showConfirmationModal: true,
    };
  default:
    return initialState();
  }
}

export function Preferences({ phoneNumber, isB2B }) {
  const {
    openErrorAlert,
    openSuccessAlert,
    closeSuccessAlert,
    openEmailUnsubSuccessAlert,
    closeEmailUnsubSuccessAlert,
    selectedUndo,
    openInfoAlert,
    openWarningAlert,
    openPendingAlert,
  } = useContext(BannerContext);

  const {
    addError
  } = useContext(RetrieveErrorContext);

  const [tableData, setTableData] = React.useState(initialSmsTableData());
  const [selected, setSelected] = React.useState([]);
  const [state, dispatch] = React.useReducer(reducer, undefined, initialState);
  const [initialSubs, setInitialSubs] = React.useState([]);
  const initialSelection = selectedUndo;
  const formattedPhone = formatPhone(phoneNumber);
  const handleRetrieve = setupHandleEmailOrSmsTelemarketingRetrieve(
    tableData, setTableData, setSelected, setInitialSubs
  );
  const scrollToAlert = useRef(null);
  const AlertInfo = () => (
    <Typography variant="body-base" ref={scrollToAlert}>
      {NO_PHONE_TEXT}
      <Link href={SECURITY_PATH}>Security</Link>
      {' page and add a phone number to your account.'}
    </Typography>
  );

  const determinePendingText = () => {
    const pro = initialSubs.find((item) => item && item.type === 'PRO')?.status;
    const promotion = initialSubs.find((item) => item && item.type === 'PROMOTIONS')?.status;
    if (pro === PENDING && promotion === PENDING) {
      return PRO_PROMO_PENDING;
    } if (pro === PENDING) {
      return PRO_PENDING;
    } if (promotion === PENDING) {
      return PROMO_PENDING;
    }
    return null;
  };

  React.useEffect(() => {
    if (initialSubs.length > 0) {
      openPendingAlert(determinePendingText(), phoneNumber, tableData);
    }
  }, [initialSubs]);

  React.useEffect(() => {
    if (initialSelection && initialSelection.length > 0) {
      closeEmailUnsubSuccessAlert();
      const subsSelected = convertSmsSubsToPrefs(tableData, initialSelection, setSelected);
      if (containsOptIn(subsSelected, tableData)) {
        dispatch({ type: Actions.USE_ACCOUNT_PHONE });
      } else {
        openSuccessAlert();
      }
    }
  }, [selectedUndo]);

  function handleError() {
    handleRollback(tableData, setTableData, selected, setSelected);
    openErrorAlert();
    dispatch({ type: Actions.RESET });
  }

  function handleRetrieveError() {
    addError();
  }

  React.useEffect(() => {
    if (phoneNumber) {
      retrieveSmsPreferences(phoneNumber, handleRetrieve, handleRetrieveError);
    } else {
      openInfoAlert(<AlertInfo />);
    }
  }, [phoneNumber]);

  function handleOptOutSuccess(res) {
    getUnsubscribeList(selected, tableData);
    handleRetrieve(res);
  }

  const handleOptInSuccess = (res) => {
    addAnalyticEvent(confirmConsentEvent);
    openSuccessAlert(SMS_OPT_IN_SUCCESS);
    handleRetrieve(res);
    dispatch({ type: Actions.RESET });
  };

  function isOptIn(title) {
    return tableData[title]?.value === YES;
  }

  const handleClose = () => {
    handleRollback(tableData, setTableData, selected, setSelected);
    dispatch({ type: Actions.RESET });
  };

  const handleModalSuccess = (res) => {
    openEmailUnsubSuccessAlert(formattedPhone, ALL_SMS_UNSUBSCRIBE_SUCCESS, initialSubs);
    handleRetrieve(res);
  };

  const clickSaveChanges = () => {

    closeSuccessAlert();
    if (!phoneNumber) {
      openWarningAlert(<AlertInfo />);
      scrollToBanner(scrollToAlert);
    } else if (selected.length) {
      if (containsOptIn(selected, tableData)) {
        dispatch({ type: Actions.USE_ACCOUNT_PHONE });
      } else {
        updateSmsPreferences(phoneNumber, selected, tableData, handleOptOutSuccess, handleError);
      }
    }
  };

  const handleUnsubscribeFromAllClick = () => {
    if (!phoneNumber) {
      openWarningAlert(<AlertInfo />);
      scrollToBanner(scrollToAlert);
    } else {
      closeSuccessAlert();
      closeEmailUnsubSuccessAlert();
      addAnalyticEvent(unsubscribeFromAllSms);
      unsubscribeFromAllMessages(phoneNumber, handleModalSuccess, handleError);
    }
  };

  const handleContinue = () => {
    dispatch({
      type: Actions.CONFIRMATION_MODAL,
      showProLegal: isOptIn(PRO_TITLE),
      showPromoLegal: isOptIn(PROMOTIONS_TITLE)
    });
  };
  const optOutB2BText = ' If you opt out of marketing text messages, '
                     + ' you may still receive transactional communications.';
  return (
    <div className="sui-grid sui-gap-4">
      <Typography variant="h2" weight="display">MARKETING TEXT MESSAGES</Typography>
      <Typography variant="body-base" height="none">
        {`To make changes to your preferences, click on the individual preference options below. You will be given
        information about how to opt into (and out of) those programs at the time that you sign up for them.`}
        {isB2B && optOutB2BText}
      </Typography>
      <MarketingPreferencesCard tableData={tableData} setTableData={setTableData} setSelected={setSelected} />
      <div className="sui-grid sui-grid-cols-8">
        <div className="hidden sm:sui-col-span-4 md:sui-col-span-5" />
        <div className="sui-col-span-8 sm:sui-col-span-4 md:sui-col-span-3 sui-grid sui-gap-y-4">
          <Button aria-label={smsSaveButton} fullWidth onClick={clickSaveChanges}>
            Save Changes
          </Button>
          <Button
            aria-label={unsubSmsButton}
            fullWidth
            variant="secondary"
            onClick={handleUnsubscribeFromAllClick}
          >
            Unsubscribe From All Marketing Text Messages
          </Button>
        </div>
      </div>

      {state.showConfirmationModal && (
        <ConfirmationModal
          phoneNumber={phoneNumber}
          handleClose={handleClose}
          handleOptInSuccess={handleOptInSuccess}
          tableData={tableData}
          selected={selected}
          proLegalState={state.showProLegal}
          promoLegalState={state.showPromoLegal}
        />
      )}
      {state.showUseAccountPhone && (
        <AccountPhoneModal
          phoneNumber={phoneNumber}
          handleClose={handleClose}
          handleContinue={handleContinue}
        />
      )}
    </div>
  );

}

function SmsPreferences({ phoneNumber, isB2B }) {

  return (
    <ErrorBoundary>
      <BannerProvider>
        <Preferences phoneNumber={phoneNumber} isB2B={isB2B} />
      </BannerProvider>
    </ErrorBoundary>
  );
}

export default SmsPreferences;

const propTypes = {
  phoneNumber: PropTypes.string,
  isB2B: PropTypes.bool
};

const defaultProps = {
  phoneNumber: null,
  isB2B: false
};

SmsPreferences.propTypes = propTypes;
SmsPreferences.defaultProps = defaultProps;
Preferences.propTypes = propTypes;
Preferences.defaultProps = defaultProps;
